import { createSelector } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';

import type { Relationship, RelationshipGroup } from '../../types/graph';
import type { RootState } from '../types';
import nodesReducer, { getGroupedNodeToGroupIdMap, getNodeGroups, getNodes, NAME as nodes } from './nodes';
import relationshipsReducer, { getVisibleRelationships, NAME as relationships } from './relationships';

export const NAME = 'graph';

const getRelWithStartAndEndIdsForNodeGroups = (
  rel: Relationship | RelationshipGroup,
  groupedNodeToGroupIdMap: Record<string, string>,
) => {
  const startGroup = groupedNodeToGroupIdMap[rel.startId];
  const endGroup = groupedNodeToGroupIdMap[rel.endId];
  if (startGroup !== undefined && startGroup === endGroup) {
    return;
  }
  return {
    ...rel,
    startId: startGroup ?? rel.startId,
    endId: endGroup ?? rel.endId,
  };
};

// Selectors
export const getNodesForRelationship = createSelector(
  (state: RootState) => getNodes(state),
  (nodes) => (rel: Relationship) => {
    const start = Object.values(nodes).find((v) => rel.startId === v.id);
    const end = Object.values(nodes).find((v) => rel.endId === v.id);
    return { start, end };
  },
);

export const getRelationshipWithNodeGroups = createSelector(
  (state: RootState) => getGroupedNodeToGroupIdMap(state),
  (groupedNodeToGroupIdMap) => (rel: Relationship | RelationshipGroup) =>
    getRelWithStartAndEndIdsForNodeGroups(rel, groupedNodeToGroupIdMap),
);

export const getNodesAndRelationshipsForGroup = createSelector(
  (state: RootState) => getNodeGroups(state),
  (state: RootState) => getVisibleRelationships(state),
  (state: RootState) => getGroupedNodeToGroupIdMap(state),
  (nodeGroups, relationships, groupedNodeToGroupIdMap) => (groupId: string) => {
    const groupedNodeIds = nodeGroups[groupId] ?? [];
    const groupedRelationships = relationships.filter((rel) => {
      const startGroup = groupedNodeToGroupIdMap[rel.startId];
      const endGroup = groupedNodeToGroupIdMap[rel.endId];
      return startGroup === groupId && endGroup === groupId;
    });
    return { nodes: groupedNodeIds, relationships: groupedRelationships };
  },
);

export const getRelationshipGroupsWithNodeGroups = createSelector(
  (state: RootState) => getVisibleRelationships(state),
  (state: RootState) => getGroupedNodeToGroupIdMap(state),
  (visibleRelationships, groupedNodeToGroupIdMap) => {
    const newRelationshipGroupMap: Record<string, RelationshipGroup> = {};
    visibleRelationships.forEach((rel) => {
      const relForGroup = getRelWithStartAndEndIdsForNodeGroups(rel, groupedNodeToGroupIdMap);
      if (!relForGroup) {
        return;
      }
      const { startId, endId } = relForGroup;
      const newRel = {
        id: `${startId}-${endId}`,
        relationships: [rel.id],
        startId,
        endId,
      };
      if (newRelationshipGroupMap[newRel.id]) {
        newRelationshipGroupMap[newRel.id]?.relationships.push(rel.id);
      } else {
        newRelationshipGroupMap[newRel.id] = newRel;
      }
    });
    return newRelationshipGroupMap;
  },
);

export const getVisibleRelationshipsForNodes = createSelector(
  (state: RootState) => getVisibleRelationships(state),
  (visibleRelationships) => (nodeIds: string[]) => {
    const relevantRelationships = visibleRelationships.filter(
      (rel) => nodeIds.includes(rel.endId) || nodeIds.includes(rel.startId),
    );

    return relevantRelationships;
  },
);

export default combineReducers({
  [nodes]: nodesReducer,
  [relationships]: relationshipsReducer,
});
